import { useCallback, useMemo } from 'react';
import { useFetch } from './use-fetch';
import {
    buildDayOptions,
    buildTimeRanges,
    extractHourMinute,
    generateSlotsInRange,
} from '../utils/helpers';
import type {
    AppointmentListResponse,
    AppointmentSlot,
    TimeRange,
} from '../models/repair-bookings';

const useAppointments = (fetcher: () => Promise<AppointmentListResponse>) => {
    const { data, error, loading } = useFetch(fetcher);

    const appointments = useMemo(() => data?.appointments ?? [], [data]);

    const { dayOptions, timeOptions } = useMemo(
        () => ({
            dayOptions: buildDayOptions(appointments),
            timeOptions: buildTimeRanges(appointments),
        }),
        [appointments]
    );

    const getSlot = useCallback(
        (rawDate: string, time: TimeRange): AppointmentSlot | null => {
            const appointment = appointments.find((a) => a.date === rawDate);
            if (!appointment) {
                return null;
            }
            return (
                appointment.slots.find((s) => {
                    const t = extractHourMinute(s.start_at);
                    return t >= time.start && t < time.end;
                }) ?? null
            );
        },
        [appointments]
    );

    const getSlotsInRange = useCallback(
        (rawDate: string, time: TimeRange): AppointmentSlot[] => {
            const appointment = appointments.find((a) => a.date === rawDate);
            return generateSlotsInRange(rawDate, time, appointment?.slots ?? []);
        },
        [appointments]
    );

    return { dayOptions, timeOptions, error, loading, getSlot, getSlotsInRange };
};

export default useAppointments;
